// «Hoy toca» (Diego, 2-nov-2026): a quién le escribe cada asesor hoy, con el mensaje ya escrito, y ahí mismo qué pasó. Todo por WhatsApp:
// el botón abre el chat y el asesor revisa y manda el mensaje él; nada sale en automático.
import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { CalendarClock, Check, MessageCircle, PhoneOff, RefreshCw, Trophy, UserX, X } from 'lucide-react';
import { MAX_DIAS_PROXIMA_FECHA, MOTIVOS_PERDIDA, MotivoPerdida, asesorDe, fechaHermosillo, sumarDias, usaHoyToca } from '@cotizador/compartido';
import type { CuerpoResultadoSeguimiento, RenglonHoyToca, RespuestaHoyToca, RespuestaTablero, Usuario } from '@cotizador/compartido';
import { api } from '../api/consultas.ts';
import { Enlace } from '../ruteo.tsx';
import { usarUsuario } from '../sesion.tsx';
import { Boton, Cargando, CampoSelect, CampoTexto, Chip, ErrorEnLinea, Vacio } from '../componentes/ui.tsx';
import { dinero, kwp, plural } from '../logica/etiquetas.ts';

type Forma = null | 'interesado' | 'compro_con_otro' | 'murio' | 'vendido';

/** El servidor dijo que hoy no le tocaba DESPUÉS de abrir WhatsApp (pestaña de ayer, o le escribieron desde otro lado). Vive en la página, no en
 *  el renglón: al volver de WhatsApp la lista se recarga y el renglón desaparece, y el aviso con él (verificación del 2-nov, ronda 2). */
interface NoLeTocaba {
  id: string;
  folio: string;
  cliente: string;
  plantilla: RenglonHoyToca['plantilla'];
  mensaje: string;
}

function Renglon({ r, delDia, propio, alCambiar, alConflicto, alNoTocaba, alListaVieja }: { r: RenglonHoyToca; delDia: string; propio: boolean; alCambiar: (aviso?: string) => void; alConflicto: (e: unknown) => void; alNoTocaba: (x: NoLeTocaba) => void; alListaVieja: () => void }) {
  const [forma, setForma] = useState<Forma>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const hoy = fechaHermosillo();
  const [fecha, setFecha] = useState(sumarDias(hoy, 7));
  const [conQuien, setConQuien] = useState('');
  const [aCuanto, setACuanto] = useState('');
  const [motivo, setMotivo] = useState<MotivoPerdida>('Dijo que no');
  const [vendida, setVendida] = useState('');
  const opciones = r.venta.pide ? r.venta.opciones : [];
  const elegida = opciones.find((o) => `${o.opcion ?? ''}|${o.nivel ?? ''}` === vendida) ?? null;
  const monto = aCuanto.trim() === '' ? null : Number(aCuanto.replace(/[$,\s]/g, ''));
  const montoMalo = monto !== null && !(Number.isFinite(monto) && monto >= 0);

  const mandar = async (cuerpo: CuerpoResultadoSeguimiento, aviso: string) => {
    setTrabajando(true);
    setError(null);
    try {
      const x = await api.resultadoSeguimiento(r.id, cuerpo);
      const tambien = x.tambien.length ? ` También se cerró ${x.tambien.join(', ')}, del mismo cliente.` : '';
      const parecidos = x.parecidos?.length ? ` Ojo: ${x.parecidos.join(', ')} se ${x.parecidos.length > 1 ? 'llaman' : 'llama'} igual con otro teléfono; si es el mismo cliente, ciérralo también.` : '';
      alCambiar(`${aviso}${tambien}${parecidos}`);
    } catch (e) {
      if ((e as { codigo?: string }).codigo === 'CONFLICTO') alConflicto(e);
      else setError(e);
      setTrabajando(false);
    }
  };

  // El enlace se abre con el mismo toque (si no, el iPhone lo bloquea); la cuenta se anota aparte. Si el servidor dice que hoy no le tocaba,
  // el WhatsApp ya se abrió: la página pregunta si sí lo mandó, con un aviso que no se borra al volver. Una lista cargada OTRO día (una
  // pestaña que se quedó abierta desde ayer) no abre nada: su mensaje era el de ayer; se recarga (verificación del 2-nov, ronda 3). Se compara
  // con el día en que ESTE aparato la cargó, no con el del servidor: con el reloj del teléfono mal puesto, el botón no se quedaría muerto.
  const abrir = (e: MouseEvent<HTMLAnchorElement>) => {
    if (delDia !== fechaHermosillo()) {
      e.preventDefault();
      alListaVieja();
      return;
    }
    void api
      .mensajeSeguimiento(r.id, r.plantilla)
      .then(() => alCambiar())
      .catch((e: unknown) => {
        if ((e as { codigo?: string }).codigo === 'CONFLICTO') alNoTocaba({ id: r.id, folio: r.folio, cliente: r.cliente_nombre, plantilla: r.plantilla, mensaje: (e as Error).message });
        else setError(e);
      });
  };

  const base = { revision: r.revision };
  return (
    <li className="hoy-cliente" data-folio={r.folio}>
      <div className="hoy-cliente__arriba">
        <div>
          <h3 className="hoy-cliente__nombre">{r.cliente_nombre}</h3>
          <p className="hoy-cliente__datos">
            {[r.kwp !== null ? kwp(r.kwp) : null, r.contado !== null ? `${dinero(r.contado)} de contado${r.promo_vencida ? ' (sin la promo: ya venció)' : ''}` : null, r.ciudad].filter(Boolean).join(' · ')}
          </p>
        </div>
        <Enlace a={`/cotizaciones/${r.id}/seguimiento`} className="hoy-cliente__folio">
          {r.folio}
        </Enlace>
      </div>
      <div className="fila-chips">
        {r.dias_sin_respuesta > 0 && (
          <Chip tono={r.dias_sin_respuesta >= 7 ? 'ambar' : 'neutro'} icono={<CalendarClock aria-hidden />}>
            {plural(r.dias_sin_respuesta, 'día', 'días')} sin respuesta
          </Chip>
        )}
        {r.mensajes_sin_respuesta > 0 && <Chip tono="neutro">{plural(r.mensajes_sin_respuesta, 'mensaje', 'mensajes')} ya</Chip>}
        {r.sin_marcar_enviada && <Chip tono="ambar">Borrador con PDF: ¿ya se la mandaste?</Chip>}
        {!propio && <Chip tono="neutro">{`De ${r.vendedor_nombre}`}</Chip>}
      </div>
      <div className="hoy-cliente__notas">
        {r.otros_folios.length > 0 && <p className="nota-suave">{`También tiene ${r.otros_folios.join(', ')}: es un solo mensaje.`}</p>}
        {r.parecidos.length > 0 && <p className="nota-suave" data-parecidos>{`${r.parecidos.join(', ')} se ${r.parecidos.length > 1 ? 'llaman' : 'llama'} igual con otro teléfono. Si es el mismo cliente (número corregido, o el de su pareja), cancela el folio que sobra para no escribirle dos veces.`}</p>}
        {r.cancelado_despues && <p className="aviso aviso--amarillo" data-cancelado-despues>{`Su folio más nuevo, ${r.cancelado_despues}, está Cancelado. Si el cliente dijo que no, toca «Murió» y ya no vuelve a salir.`}</p>}
        {r.promo_vencida && <p className="aviso aviso--amarillo" data-promo-vencida>Su promo ya venció: quítala en la cotización antes de mandarle otro PDF.</p>}
      </div>
      <p className="hoy-cliente__mensaje">{r.texto}</p>
      {!propio ? (
        // El mensaje va firmado por su asesor («Soy Juan Carlos, de Solarix»): mandarlo desde otro WhatsApp diría quién no es (verificación del 2-nov, ronda 2).
        <p className="nota-suave" data-de-otro>{`Le escribe ${asesorDe(r.vendedor_nombre)}: el mensaje va con su nombre.`}</p>
      ) : r.whatsapp_url ? (
        <a className="boton boton--principal hoy-cliente__whatsapp" href={r.whatsapp_url} target="_blank" rel="noopener noreferrer" onClick={abrir}>
          <MessageCircle aria-hidden />
          <span>Abrir WhatsApp</span>
        </a>
      ) : (
        <p className="aviso aviso--amarillo hoy-cliente__sin-numero">
          <PhoneOff aria-hidden />
          <span>
            {r.telefono ? `El teléfono ${r.telefono} no sirve para WhatsApp. ` : 'No tiene teléfono. '}
            <Enlace a={`/cotizaciones/${r.id}/cuestionario/editar`}>Corrígelo aquí</Enlace>.
          </span>
        </p>
      )}
      <div className="hoy-cliente__resultados" role="group" aria-label={`Qué pasó con ${r.cliente_nombre}`}>
        <Boton chico variante="secundario" icono={<Check aria-hidden />} cargando={trabajando && forma === null} onClick={() => void mandar({ ...base, accion: 'contesto' }, `${r.cliente_nombre}: contestó. Se le busca en 3 días.`)}>
          Contestó
        </Boton>
        <Boton chico variante="secundario" icono={<CalendarClock aria-hidden />} onClick={() => setForma(forma === 'interesado' ? null : 'interesado')}>
          Sigue interesado
        </Boton>
        <Boton chico variante="secundario" icono={<UserX aria-hidden />} onClick={() => setForma(forma === 'compro_con_otro' ? null : 'compro_con_otro')}>
          Compró con otro
        </Boton>
        <Boton chico variante="secundario" icono={<X aria-hidden />} onClick={() => setForma(forma === 'murio' ? null : 'murio')}>
          Murió
        </Boton>
        <Boton chico variante="secundario" icono={<Trophy aria-hidden />} onClick={() => setForma(forma === 'vendido' ? null : 'vendido')}>
          Vendido
        </Boton>
      </div>
      {forma === 'interesado' && (
        <div className="hoy-cliente__forma">
          <CampoTexto etiqueta="¿Cuándo le escribes?" type="date" min={sumarDias(hoy, 1)} max={sumarDias(hoy, MAX_DIAS_PROXIMA_FECHA)} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          <Boton chico variante="principal" cargando={trabajando} disabled={!fecha || fecha <= hoy || fecha > sumarDias(hoy, MAX_DIAS_PROXIMA_FECHA)} onClick={() => void mandar({ ...base, accion: 'interesado', fecha }, `${r.cliente_nombre}: sigue interesado.`)}>
            Guardar
          </Boton>
        </div>
      )}
      {forma === 'compro_con_otro' && (
        <div className="hoy-cliente__forma">
          <CampoTexto etiqueta="¿Con quién? (si lo dijo)" maxLength={80} value={conQuien} onChange={(e) => setConQuien(e.target.value)} />
          <CampoTexto etiqueta="¿A cuánto? (si lo dijo)" inputMode="numeric" error={montoMalo ? 'Escribe solo el número.' : null} value={aCuanto} onChange={(e) => setACuanto(e.target.value)} />
          <Boton chico variante="principal" cargando={trabajando} disabled={montoMalo} onClick={() => void mandar({ ...base, accion: 'compro_con_otro', con_quien: conQuien.trim() || null, a_cuanto: monto }, `${r.cliente_nombre}: se cerró, compró con otro.`)}>
            Guardar
          </Boton>
        </div>
      )}
      {forma === 'murio' && (
        <div className="hoy-cliente__forma">
          <CampoSelect etiqueta="¿Por qué?" value={motivo} onChange={(e) => setMotivo(MotivoPerdida.parse(e.target.value))} opciones={MOTIVOS_PERDIDA.map((m) => ({ valor: m, texto: m }))} />
          <Boton chico variante="principal" cargando={trabajando} onClick={() => void mandar({ ...base, accion: 'murio', motivo }, `${r.cliente_nombre}: se cerró. Ya no vuelve a salir.`)}>
            Guardar
          </Boton>
        </div>
      )}
      {forma === 'vendido' && (
        <div className="hoy-cliente__forma">
          {r.venta.pide ? (
            <CampoSelect
              etiqueta={r.venta.pide === 'nivel' ? '¿Qué nivel de baterías se vendió?' : '¿Qué opción se vendió?'}
              value={vendida}
              onChange={(e) => setVendida(e.target.value)}
              opciones={[{ valor: '', texto: 'Elige…' }, ...opciones.map((o) => ({ valor: `${o.opcion ?? ''}|${o.nivel ?? ''}`, texto: o.texto }))]}
            />
          ) : (
            <p className="nota-suave">{r.venta.motivo}</p>
          )}
          <Boton
            chico
            variante="principal"
            cargando={trabajando}
            disabled={!!r.venta.pide && !elegida}
            onClick={() => void mandar({ ...base, accion: 'vendido', ...(elegida ? { venta: { opcion: elegida.opcion, nivel: elegida.nivel } } : {}) }, `${r.cliente_nombre}: ¡vendido!`)}
          >
            Marcar vendido
          </Boton>
        </div>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </li>
  );
}

function Tablero({ t }: { t: RespuestaTablero }) {
  const hayMotivos = t.motivos.length > 0;
  return (
    <section className="hoja pila hoy-tablero" aria-label="Tablero de seguimiento">
      <h2 className="hoy-tablero__titulo">Cómo van hoy</h2>
      {t.filas.length === 0 ? (
        <p className="nota-suave">Hoy no le toca seguimiento a nadie.</p>
      ) : (
        <div className="hoy-tablero__desliza">
        <table className="tabla tabla--compacta">
          <thead>
            <tr>
              <th scope="col">Vendedor</th>
              <th scope="col" className="num">
                Le tocaban hoy
              </th>
              <th scope="col" className="num">
                Atendió
              </th>
              <th scope="col" className="num">
                Pendientes en total
              </th>
            </tr>
          </thead>
          <tbody>
            {t.filas.map((f) => (
              <tr key={f.vendedor_id}>
                <th scope="row">{f.nombre}</th>
                <td className="num">{f.le_tocaban}</td>
                <td className="num">{f.atendidas}</td>
                <td className="num">{f.pendientes_total}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {hayMotivos && (
        <div className="pila pila--chica">
          <h3 className="hoy-tablero__sub">Por qué se perdieron, últimos 30 días</h3>
          <div className="fila-chips">
            {t.motivos.map((m) => (
              <Chip key={m.motivo} tono="neutro">{`${m.motivo}: ${m.cuantos}`}</Chip>
            ))}
          </div>
        </div>
      )}
      {t.con_otro.length > 0 && (
        <div className="pila pila--chica">
          <h3 className="hoy-tablero__sub">Compraron con otro</h3>
          <ul className="hoy-tablero__lista">
            {t.con_otro.slice(0, 10).map((x) => (
              <li key={x.folio}>
                {`${x.cliente} (${x.vendedor})${x.con_quien ? `: ${x.con_quien}` : ''}${x.a_cuanto !== null ? `, ${dinero(x.a_cuanto)}` : ''}`}
              </li>
            ))}
          </ul>
        </div>
      )}
      {t.lectura_incompleta && <p className="aviso aviso--amarillo">Hay más de 500 cotizaciones: este tablero solo cuenta las 500 más recientes.</p>}
    </section>
  );
}

export function HoyToca() {
  const { puede, usuario } = usarUsuario();
  const esDiego = puede('ver_costos');
  const [vendedor, setVendedor] = useState<string>(esDiego ? 'todos' : usuario.id);
  const [datos, setDatos] = useState<RespuestaHoyToca | null>(null);
  const [tablero, setTablero] = useState<RespuestaTablero | null>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [noTocaba, setNoTocaba] = useState<NoLeTocaba | null>(null);
  const [anotando, setAnotando] = useState(false);
  const [errorAnotar, setErrorAnotar] = useState<unknown>(null);
  /** El día (en este aparato) en que se cargó la lista que se ve. */
  const [cargadaEl, setCargadaEl] = useState('');
  /** Diego viendo la lista de otro (o la de todos): lo que se le dice al asesor («te toca», «escribiste») ahí no es de él. */
  const ajena = esDiego && vendedor !== usuario.id;

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const [d, t] = await Promise.all([api.hoyToca(esDiego ? vendedor : undefined), esDiego ? api.tableroSeguimiento() : Promise.resolve(null)]);
      setDatos(d);
      setTablero(t);
      setCargadaEl(fechaHermosillo());
    } catch (e) {
      setError(e);
    }
  }, [esDiego, vendedor]);

  useEffect(() => {
    void cargar();
  }, [cargar]);
  // Al volver de WhatsApp (o a una pestaña abierta desde ayer) la lista se pide otra vez: con la de antes, el asesor le volvería a escribir
  // a quien ya le escribió (verificación adversarial del 2-nov).
  useEffect(() => {
    const alVolver = () => {
      if (document.visibilityState === 'visible') void cargar();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [cargar]);
  useEffect(() => {
    if (esDiego) void api.usuarios().then((r) => setUsuarios(r.usuarios)).catch(() => setUsuarios([]));
  }, [esDiego]);

  const alCambiar = (texto?: string) => {
    if (texto) setAviso(texto);
    void cargar();
  };
  const alConflicto = () => {
    setAviso('Alguien más ya tocó ese cliente: se recargó la lista.');
    void cargar();
  };
  const alListaVieja = () => {
    setAviso('Esta lista era de otro día: ya se recargó con la de hoy. Revisa el mensaje y vuelve a abrir WhatsApp.');
    void cargar();
  };
  const alNoTocaba = (x: NoLeTocaba) => {
    setErrorAnotar(null);
    setNoTocaba(x);
    void cargar();
  };
  const siLoMande = async () => {
    if (!noTocaba) return;
    setAnotando(true);
    setErrorAnotar(null);
    try {
      await api.mensajeSeguimiento(noTocaba.id, noTocaba.plantilla, true);
      setAviso(`${noTocaba.cliente}: quedó anotado el mensaje.`);
      setNoTocaba(null);
      void cargar();
    } catch (e) {
      setErrorAnotar(e);
    } finally {
      setAnotando(false);
    }
  };

  const conteo = datos ? (datos.vendedor_id === 'todos' ? `${plural(datos.renglones.length, 'cliente', 'clientes')} por escribir · ${plural(datos.atendidas_hoy, 'atendido', 'atendidos')}` : `${plural(datos.renglones.length, 'cliente', 'clientes')} por escribir · ${datos.atendidas_hoy} de ${datos.tope} hoy`) : '';

  return (
    <div className="pagina pila pila--grande hoy-toca">
      <header className="pagina__cabeza">
        <div>
          <h1 className="pagina__titulo">Hoy toca</h1>
          {datos && <p className="pagina__resumen">{conteo}</p>}
        </div>
        <Boton chico variante="fantasma" icono={<RefreshCw aria-hidden />} onClick={() => void cargar()}>
          Actualizar
        </Boton>
      </header>
      {esDiego && (
        <CampoSelect
          className="hoy-toca__quien"
          etiqueta="Ver la lista de"
          value={vendedor}
          onChange={(e) => {
            setDatos(null);
            setVendedor(e.target.value);
          }}
          // Solo quien usa «Hoy toca»: un externo no la tiene y sus clientes no salen (verificación del 2-nov, ronda 3).
          opciones={[{ valor: 'todos', texto: 'Todos' }, ...usuarios.filter((u) => usaHoyToca({ rol: u.rol })).map((u) => ({ valor: u.id, texto: u.nombre }))]}
        />
      )}
      {tablero && <Tablero t={tablero} />}
      {aviso && (
        <p className="aviso aviso--verde hoy-toca__aviso" role="status">
          {aviso}
        </p>
      )}
      {noTocaba && (
        <div className="aviso aviso--amarillo hoy-toca__pendiente" role="alert" data-no-tocaba={noTocaba.folio}>
          <p>{`${noTocaba.mensaje} Si ya le mandaste el WhatsApp a ${noTocaba.cliente}, anótalo: así no le vuelve a tocar antes de tiempo.`}</p>
          <div className="fila-botones">
            <Boton chico variante="principal" cargando={anotando} onClick={() => void siLoMande()}>
              Sí lo mandé: anótalo
            </Boton>
            <Boton chico variante="secundario" disabled={anotando} onClick={() => setNoTocaba(null)}>
              No lo mandé
            </Boton>
          </div>
          <ErrorEnLinea error={errorAnotar} alCerrar={() => setErrorAnotar(null)} />
        </div>
      )}
      <ErrorEnLinea error={error} alRecargar={() => void cargar()} alCerrar={() => setError(null)} />
      {!datos && !error && <Cargando etiqueta="Buscando a quién te toca escribir…" />}
      {datos?.lectura_incompleta && <p className="aviso aviso--amarillo">Hay más de 500 cotizaciones: esta lista solo revisa las 500 más recientes. Avísale a Diego.</p>}
      {datos && datos.renglones.length === 0 && (
        <Vacio titulo={datos.para_despues > 0 ? (ajena ? 'Ya se completaron los de hoy' : 'Ya completaste los de hoy') : ajena ? 'Hoy no hay a quién escribirle en esta lista' : 'Hoy no te toca escribirle a nadie'}>
          {datos.para_despues > 0 ? `${ajena ? 'Se llegó' : 'Llegaste'} a ${datos.tope} clientes. ${plural(datos.para_despues, 'queda', 'quedan')} para mañana.` : 'Cuando a un cliente le toque seguimiento, aparece aquí con su mensaje listo.'}
        </Vacio>
      )}
      {datos && datos.renglones.length > 0 && (
        <ul className="hoy-toca__lista">
          {datos.renglones.map((r) => (
            <Renglon key={`${r.id}-${r.revision}`} r={r} delDia={cargadaEl} propio={r.vendedor_id === usuario.id} alCambiar={alCambiar} alConflicto={alConflicto} alNoTocaba={alNoTocaba} alListaVieja={alListaVieja} />
          ))}
        </ul>
      )}
      {datos && datos.para_despues > 0 && datos.renglones.length > 0 && <p className="nota-suave">{`${plural(datos.para_despues, 'cliente más', 'clientes más')} para los siguientes días: 20 al día.`}</p>}
      {datos && datos.escritas_hoy.length > 0 && (
        <section className="pila" aria-label="Ya escritos hoy">
          <h2 className="hoy-tablero__titulo">{ajena ? 'Ya se les escribió hoy: ¿qué pasó?' : 'Ya les escribiste hoy: ¿qué pasó?'}</h2>
          <ul className="hoy-toca__lista">
            {datos.escritas_hoy.map((r) => (
              <Renglon key={`${r.id}-${r.revision}`} r={r} delDia={cargadaEl} propio={r.vendedor_id === usuario.id} alCambiar={alCambiar} alConflicto={alConflicto} alNoTocaba={alNoTocaba} alListaVieja={alListaVieja} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
